import { Pause, Play } from "lucide-react";
import { formatTimecode } from "@/lib/format";
// The same bound the player actually stops at, named once rather than repeated here.
import { MAX_SAMPLE_MS as MAX_CLIP_MS } from "@/lib/useAudioFragment";

/** The handoff draws 34 of them, 18 px tall, 2 px apart. */
const BARS = 34;

/**
 * UI-16's fragment player — six seconds of a meeting, on a card that has no transcript
 * around it.
 *
 * Two halves, and only one of them is real. **The fill is real**: it is the audio
 * element's own `currentTime` against this fragment's own bounds (`useAudioFragment`),
 * which is what the milestone means by "not a synthetic timer" — a clip that stalls or a
 * browser that refuses playback leaves the bars where they are rather than sweeping on
 * through silence. **The bar heights are decoration**: nothing in this system has a
 * waveform of a recording, and the alternative to a fixed shape is not a truer shape but a
 * random one that looks like data. The formula is the handoff's, deliberately identical for
 * every clip, so it reads as an ornament rather than as a picture of this particular voice.
 */
export function FragmentPlayer({
  startMs,
  endMs,
  playing,
  progress,
  onToggle,
  disabled,
}: {
  startMs: number;
  /** Null when the turn has no end — the clip is then bounded by the sample length. */
  endMs: number | null;
  playing: boolean;
  /** 0 to 1 through the clip, from the audio element itself. */
  progress: number;
  onToggle: () => void;
  disabled?: boolean;
}) {
  const Icon = playing ? Pause : Play;

  return (
    <div className="flex items-center gap-[var(--space-3)] rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3 py-2">
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        title="Play this fragment"
        aria-label={playing ? "Stop this fragment" : "Play this fragment"}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-full)] border border-[var(--border-subtle)] bg-[var(--surface-2)] transition hover:brightness-125 disabled:opacity-40"
        style={{ color: "var(--vl-bridge-soft)" }}
      >
        <Icon size={14} strokeWidth={2.5} />
      </button>

      <div aria-hidden className="flex h-[18px] min-w-0 flex-1 items-center gap-[2px]">
        {Array.from({ length: BARS }, (_, index) => (
          <span
            key={index}
            className="flex-1 rounded-[1px] transition-[background-color] duration-[120ms] ease-linear"
            style={{
              height: barHeight(index),
              background: index / BARS < progress ? "var(--vl-bridge)" : "var(--border-strong)",
            }}
          />
        ))}
      </div>

      <span className="shrink-0 font-mono text-[length:var(--text-2xs)] text-[var(--text-muted)]">
        {formatTimecode(startMs)} · {clipLength(startMs, endMs)}
      </span>
    </div>
  );
}

/** The handoff's shape: two sine waves summed, 4 px to 15 px. */
function barHeight(index: number): number {
  return 4 + Math.round(11 * Math.abs(Math.sin(index * 1.7) * 0.6 + Math.sin(index * 0.6) * 0.4));
}

/**
 * "0:06" — how long this clip actually is.
 *
 * The same bound `useAudioFragment` plays to, rather than the turn's full length: a
 * four-minute answer is still an eight-second sample, and a label saying 4:12 over a clip
 * that stops at six seconds would be the player lying about itself.
 */
function clipLength(startMs: number, endMs: number | null): string {
  const span = Math.min(endMs === null ? Infinity : endMs - startMs, MAX_CLIP_MS);
  const seconds = Math.max(Math.round(span / 1000), 0);

  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}
